// UX14-F2 (docs/build-briefs/UX14-F2-keyboard-safe-sheets.md). Safari on iPad
// ignores `interactive-widget` and shrinks only window.visualViewport when the
// on-screen keyboard rises. This publishes how much of the layout viewport the
// keyboard covers so the scrims and sheets in styles.css can shrink to the
// visible area. No DOM is read here beyond the root element and the focused
// field; the arithmetic is pure and unit tested.

/** Browser chrome (address bar, pinch zoom) moves the visual viewport by a few
 * dozen px; a keyboard never covers less than this. */
export const KEYBOARD_MIN_OCCLUDED_PX = 80;

export interface VisualViewportLike {
  readonly height: number;
  readonly offsetTop: number;
}

export interface KeyboardInset {
  /** Pixels at the bottom of the layout viewport the keyboard covers. */
  readonly occluded: number;
  /** Pixels at the top of the layout viewport scrolled out of view. */
  readonly visibleTop: number;
}

export function keyboardInset(layoutHeight: number, viewport: VisualViewportLike): KeyboardInset {
  const visibleTop = Math.max(0, Math.round(viewport.offsetTop));
  const occluded = Math.max(0, Math.round(layoutHeight - viewport.height - viewport.offsetTop));
  if (occluded < KEYBOARD_MIN_OCCLUDED_PX) {
    return { occluded: 0, visibleTop: 0 };
  }
  return { occluded, visibleTop };
}

function isTextField(element: Element | null): element is HTMLElement {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  );
}

/** Keeps the focused field of an open sheet inside the visible area. */
function keepFocusedFieldVisible(): void {
  const active = document.activeElement;
  if (isTextField(active) && active.closest(".scrim, .confirm-scrim") !== null) {
    active.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
}

/** Starts listening; returns a function that stops and clears the variables.
 * Does nothing where there is no visualViewport. */
export function installKeyboardInset(): () => void {
  const viewport = window.visualViewport;
  const root = document.documentElement;
  if (viewport === null || viewport === undefined) {
    return () => undefined;
  }
  const update = (): void => {
    const inset = keyboardInset(window.innerHeight, viewport);
    root.style.setProperty("--keyboard-occluded-height", `${inset.occluded}px`);
    root.style.setProperty("--keyboard-visible-top", `${inset.visibleTop}px`);
    root.classList.toggle("keyboard-up", inset.occluded > 0);
    if (inset.occluded > 0) {
      window.requestAnimationFrame(keepFocusedFieldVisible);
    }
  };
  viewport.addEventListener("resize", update);
  viewport.addEventListener("scroll", update);
  update();
  return () => {
    viewport.removeEventListener("resize", update);
    viewport.removeEventListener("scroll", update);
    root.style.removeProperty("--keyboard-occluded-height");
    root.style.removeProperty("--keyboard-visible-top");
    root.classList.remove("keyboard-up");
  };
}
